import { t } from "../../i18n";
import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Alert, Button, Divider, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ProfileSection from './ProfileSection';
import ResetPasswordDialog from './ResetPasswordDialog';
import { useAuth } from '../../auth/AuthProvider';
import { changeOwnPassword, passwordErrorMessage } from '../../services/profile';
export default function PasswordForm() {
  const { user } = useAuth();
  const [resetOpen, setResetOpen] = useState(false);
  const [visible, setVisible] = useState({});
  const [notice, setNotice] = useState(null);
  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required('Introduza a palavra-passe atual.'),
      newPassword: Yup.string().required('Introduza a nova palavra-passe.').max(256, 'Utilize até 256 caracteres.').notOneOf([Yup.ref('currentPassword')], 'Escolha uma palavra-passe diferente da atual.'),
      confirmPassword: Yup.string().required('Confirme a nova palavra-passe.').oneOf([Yup.ref('newPassword')], 'As palavras-passe devem coincidir.')
    }),
    onSubmit: async (values, {
      resetForm,
      setSubmitting
    }) => {
      setNotice(null);
      try {
        await changeOwnPassword(values.currentPassword, values.newPassword);
        resetForm();
        setVisible({});
        setNotice({
          severity: 'success',
          text: 'A palavra-passe foi alterada com sucesso.'
        });
      } catch (error) {
        setNotice({
          severity: 'error',
          text: passwordErrorMessage(error)
        });
      } finally {
        setSubmitting(false);
      }
    }
  });
  const field = name => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: Boolean(formik.touched[name] && formik.errors[name]),
    helperText: t(formik.touched[name] && formik.errors[name]),
    disabled: formik.isSubmitting || resetOpen,
    fullWidth: true
  });
  const passwordField = (name, label) => ({
    ...field(name),
    type: visible[name] ? 'text' : 'password',
    InputProps: {
      endAdornment: <InputAdornment position="end">
                <IconButton type="button" disabled={formik.isSubmitting || resetOpen} aria-label={(visible[name] ? t('Ocultar ') : t('Mostrar ')) + t(label)} aria-pressed={Boolean(visible[name])} onMouseDown={event => event.preventDefault()} onClick={() => setVisible(fields => ({
          ...fields,
          [name]: !fields[name]
        }))}>
                    {visible[name] ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </InputAdornment>
    }
  });
  return <ProfileSection title={t("Segurança")} description={t("Altere a palavra-passe utilizada para entrar na sua conta.")}>
            <Stack component="form" noValidate onSubmit={formik.handleSubmit} spacing={2.5}>
                <TextField label={t("Palavra-passe atual")} autoComplete="current-password" {...passwordField('currentPassword', t('palavra-passe atual'))} />
                <TextField label={t("Nova palavra-passe")} autoComplete="new-password" {...passwordField('newPassword', t('nova palavra-passe'))} />
                <TextField label={t("Confirmar nova palavra-passe")} autoComplete="new-password" {...passwordField('confirmPassword', t('confirmação da nova palavra-passe'))} />
                {notice && <Alert severity={notice.severity}>{t(notice.text)}</Alert>}
                <Button type="submit" variant="contained" disabled={formik.isSubmitting || resetOpen}>
                    {formik.isSubmitting ? t('A alterar…') : t('Alterar palavra-passe')}
                </Button>
            </Stack>
            <Divider sx={{ my: 3 }} />
            <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                    {t('Não sabe a palavra-passe atual ou entra apenas por código?')}
                </Typography>
                <Button type="button" variant="outlined" disabled={formik.isSubmitting || !user?.email}
                    onClick={() => { setNotice(null); setResetOpen(true); }}>
                    {t('Redefinir palavra-passe por email')}
                </Button>
            </Stack>
            {resetOpen && <ResetPasswordDialog key={user.uuid} user={user}
                onClose={() => setResetOpen(false)} onSuccess={() => {
                    formik.resetForm();
                    setVisible({});
                    setResetOpen(false);
                    setNotice({ severity: 'success', text: 'A palavra-passe foi redefinida com sucesso.' });
                }} />}
        </ProfileSection>;
}
